import axios from "axios";
import { apiEndpoint } from "@iso/lib/helpers/endpoint";
import { authHeader } from "@iso/lib/helpers/authHeader";

export const contractorService = {
  getByProperty,
  getOne,
  getWorkLocationOptions,
  create,
  update,
  changeStatus,
  accessCandidates,
  approveWithAccess,
  businessOptions,
  assignUser,
  workerAccess,
  destroy,
};

function getWorkLocationOptions(propertyId) {
  return axios
    .get(`${apiEndpoint.contractors}/work-location-options`, {
      headers: authHeader(),
      params: { property_id: propertyId },
    })
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

function getByProperty(propertyId, params = {}) {
  return axios
    .get(`${apiEndpoint.contractors}/get-by-property`, {
      headers: authHeader(),
      params: { property_id: propertyId, ...params },
    })
    .then((res) => {
      if (res && res.status === 200 && res.data.code === "0000") {
        return res.data;
      }
      return res?.data || {};
    });
}

function getOne(id) {
  return axios
    .get(`${apiEndpoint.contractors}/view`, {
      headers: authHeader(),
      params: { id },
    })
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

function create(contractor) {
  return axios
    .post(
      `${apiEndpoint.contractors}/create`,
      { contractor },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

function update(id, contractor) {
  return axios
    .post(
      `${apiEndpoint.contractors}/update`,
      { id, contractor },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

/**
 * The contractor's personnel, with whether each can sign in today and whether
 * they could be given a login — feeds the confirmation modal on Approve
 * Contractor. Also reports whether the property is on manual approval, which is
 * the only mode where that modal has anything to decide.
 */
function accessCandidates(id) {
  return axios
    .get(`${apiEndpoint.contractors}/access-candidates`, {
      headers: authHeader(),
      params: { id },
    })
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

/**
 * Approve the contractor AND grant a login to the named personnel, in one step.
 * `workerIds` may be empty — approving the business without granting anybody
 * access is what the button did before the modal existed.
 */
function approveWithAccess(id, workerIds) {
  return axios
    .post(
      `${apiEndpoint.contractors}/approve-with-access`,
      { id, worker_ids: workerIds },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

/** id + name for every contractor business on a property, for the assign picker. */
function businessOptions(propertyId) {
  return axios
    .get(`${apiEndpoint.contractors}/business-options`, {
      headers: authHeader(),
      params: { property_id: propertyId },
    })
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

/**
 * Put a contractor user account into a contractor business. Only valid for a
 * user with no business yet — the API refuses an overwrite, so the UI offers the
 * control only when the field is empty.
 */
function assignUser(propertyId, userId, contractorId) {
  return axios
    .post(
      `${apiEndpoint.contractors}/assign-user`,
      { property_id: propertyId, user_id: userId, contractor_id: contractorId },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

/**
 * Grant or revoke one worker's login for their contractor's property.
 * grant=true creates an account if needed and emails a temp password;
 * grant=false removes the property access but leaves the account alone.
 */
function workerAccess(workerId, grant) {
  return axios
    .post(
      `${apiEndpoint.contractors}/worker-access`,
      { worker_id: workerId, grant },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

function changeStatus(id, status) {
  return axios
    .post(
      `${apiEndpoint.contractors}/change-status`,
      { id, status },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : {}));
}

function destroy(id) {
  return axios
    .post(
      `${apiEndpoint.contractors}/delete`,
      { id },
      { headers: authHeader() }
    )
    .then((res) => (res && res.status === 200 ? res.data : {}));
}
